import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import { useMediaQuery } from '@mui/system';
import { useEffect, useEffectEvent, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, TextField } from '@/components';
import { Modals, Routes } from '@/constants';
import { checkmark, checkmarkGreen } from '@/images';
import { AnalyticsService, EVENTS, log } from '@/services';
import {
  uiActions,
  useFetchPurchaseFeedbackOptionsQuery,
  useHasSubmittedPurchaseFeedbackQuery,
  useLazyPollForCheckoutSessionCompletionQuery,
  useSubmitPurchaseFeedbackMutation,
} from '@/store';
import { SnackbarUtils } from '@/utils';

import { SelectDropdown } from './components';

export const PurchaseSuccess = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { data } = useFetchPurchaseFeedbackOptionsQuery();
  const [isCheckoutComplete, setIsCheckoutComplete] = useState(false);
  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [textInputValue, setTextInputValue] = useState<string>('');
  const options = data?.reduce(
    (acc, option) => {
      acc[option.howdyhauOptionId] = {
        text: option.label,
        detailFieldLabel: option.detailFieldLabel,
        detailFieldPlaceholder: option.detailFieldPlaceholder,
      };
      return acc;
    },
    {} as Record<
      string,
      {
        text: string;
        detailFieldLabel: string;
        detailFieldPlaceholder?: string;
      }
    >,
  );

  const { search } = useLocation();
  const { checkout_session_id: checkoutSessionId, is_b2c = 'false' } =
    Object.fromEntries(new URLSearchParams(search).entries());

  const { data: hasRecentlySubmittedPurchaseFeedback } =
    useHasSubmittedPurchaseFeedbackQuery();
  const [pollForCheckoutSessionCompletion] =
    useLazyPollForCheckoutSessionCompletionQuery();
  const [submit, { isLoading }] = useSubmitPurchaseFeedbackMutation();

  const isB2C = is_b2c === 'true';

  const waitForCheckoutSessionCompletion = useEffectEvent(async () => {
    if (checkoutSessionId) {
      try {
        await pollForCheckoutSessionCompletion({
          checkoutSessionId,
        }).unwrap();
        setIsCheckoutComplete(true);
      } catch {
        SnackbarUtils.error(
          `Failed to create ${isB2C ? 'plan' : 'team'}. Please contact support.`,
        );
        navigate(Routes.PROFILE);
      }
    } else {
      SnackbarUtils.error('Purchase failed. Please contact support.');
      navigate(Routes.PROFILE);
    }
  });

  const logHdyhauAbort = useEffectEvent(() => {
    AnalyticsService.logEvent(EVENTS.PRICING_EVENTS.HDYHAU_ABORT, {
      hdyhau_value: selectedOption,
      hdyhau_detail: textInputValue,
    });
  });

  useEffect(() => {
    waitForCheckoutSessionCompletion();
  }, [search]);

  useEffect(() => {
    return () => {
      if (!isFeedbackSubmitted && !hasRecentlySubmittedPurchaseFeedback) {
        logHdyhauAbort();
      }
    };
  }, [isFeedbackSubmitted, hasRecentlySubmittedPurchaseFeedback]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
      <Box
        sx={{
          maxWidth: 670,
          width: '100%',
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isMobile ? 1.5 : 2,
            mt: isMobile ? 4 : 8,
            textAlign: 'center',
          }}>
          {isCheckoutComplete ? (
            <>
              <Box
                component="img"
                src={checkmark}
                alt="Success checkmark"
                sx={{
                  width: isMobile ? 40 : 72,
                  height: isMobile ? 40 : 72,
                }}
              />
              <Typography variant={isMobile ? 'h2' : 'hero1'}>
                You&apos;re all set!
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.main' }}>
                Your purchase was successful and your plan is now active.
                <br />A receipt has been sent to your email.{' '}
              </Typography>
            </>
          ) : (
            <>
              <Skeleton variant="circular" width={72} height={72} />
              <Skeleton variant="text" width={260} height={56} />
              <Skeleton variant="text" width={460} height={28} />
              <Skeleton variant="text" width={380} height={28} />
            </>
          )}
        </Box>
        <Box
          sx={{
            width: '100%',
            height: '1px',
            backgroundColor: 'grey50',
            my: isMobile ? 4 : 5,
          }}
        />
        {isFeedbackSubmitted || hasRecentlySubmittedPurchaseFeedback ? (
          <Box>
            <Box
              sx={{
                display: 'flex',
                padding: '15px',
                alignItems: 'flex-start',
                gap: '10px',
                borderRadius: 2,
                border: theme => `1px solid ${theme.palette.green10}`,
                bgcolor: 'green50',
              }}>
              <Box
                component={'img'}
                src={checkmarkGreen}
                alt="Checkmark"
                sx={{ width: 21, height: 21 }}
              />
              <Typography variant="body2SemiBold" sx={{ color: 'green10' }}>
                Thanks for letting us know!
              </Typography>
            </Box>
            <Box
              sx={{
                mt: isMobile ? 4 : 5,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
              }}>
              <Button
                variant="secondary"
                label="Explore Episodes"
                fullWidth={isMobile}
                skinny={isMobile}
                onClick={() => {
                  navigate(Routes.DISCOVER);
                }}
              />
              <Button
                variant="primary"
                sx={{
                  bgcolor: 'secondary.main',
                  color: 'white',
                }}
                label={isB2C ? 'Share with Friends' : 'Invite Your Team'}
                fullWidth={isMobile}
                skinny={isMobile}
                onClick={() => {
                  if (isB2C) {
                    dispatch(
                      uiActions.setActiveModal({
                        name: Modals.SHARE_WITH_A_FRIEND,
                      }),
                    );
                  } else {
                    navigate(Routes.MEMBER_MANAGEMENT);
                  }
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              padding: isMobile ? 2.5 : 4,
              gap: isMobile ? 2.5 : 3,
              borderRadius: 2,
              borderColor: 'secondary.active',
              borderWidth: 1,
              borderStyle: 'solid',
              bgcolor: 'secondary.light',
              width: '100%',
            }}>
            {isCheckoutComplete ? (
              <>
                <Box>
                  <Typography variant="h3">
                    One quick question before you dive in
                  </Typography>
                  <Typography
                    variant="body3"
                    sx={{ color: 'text.main', mt: 1 }}>
                    How did you first hear about LumiQ? This helps us reach more
                    accounting and finance professionals like you.
                  </Typography>
                </Box>
                <Box>
                  <SelectDropdown
                    options={options || {}}
                    selectedOption={selectedOption}
                    onSave={option => {
                      setSelectedOption(option);
                      AnalyticsService.logEvent(
                        EVENTS.PRICING_EVENTS.HDYHAU_DROPDOWN_SELECT,
                        {
                          hdyhau_value: option,
                        },
                      );
                    }}
                  />
                  {options?.[selectedOption] && (
                    <Box
                      sx={{
                        mt: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}>
                      <Typography variant="body3" sx={{ color: 'text.main' }}>
                        {options[selectedOption].detailFieldLabel}
                      </Typography>
                      <TextField
                        value={textInputValue}
                        onChange={e => setTextInputValue(e.target.value)}
                        type="text"
                        placeholder={
                          options[selectedOption].detailFieldPlaceholder ||
                          'Please provide your answer here...'
                        }
                        multiline
                        rows={4}
                        fullWidth
                      />
                    </Box>
                  )}
                </Box>
                <Button
                  variant="primary"
                  disabled={!selectedOption}
                  sx={{ alignSelf: 'flex-end' }}
                  label="Submit"
                  fullWidth={false}
                  skinny={false}
                  isLoading={isLoading}
                  onClick={async () => {
                    if (!checkoutSessionId) {
                      SnackbarUtils.error(
                        'Checkout session ID is missing. Please contact support.',
                      );
                      return;
                    }
                    try {
                      await submit({
                        howdyhauOptionId: selectedOption,
                        detailResponse: textInputValue,
                        checkoutSessionId,
                      }).unwrap();
                    } catch (error) {
                      log('Failed to submit purchase feedback', { error });
                    } finally {
                      setIsFeedbackSubmitted(true);
                      AnalyticsService.logEvent(
                        EVENTS.PRICING_EVENTS.HDYHAU_SUBMIT,
                        {
                          hdyhau_value: selectedOption,
                          hdyhau_detail: textInputValue,
                        },
                      );
                    }
                  }}
                />
              </>
            ) : (
              <>
                {' '}
                <Box>
                  <Skeleton variant="text" width={340} height={40} />
                  <Skeleton variant="text" width="100%" height={28} />
                  <Skeleton variant="text" width="78%" height={28} />
                </Box>
                <Skeleton variant="rounded" width="100%" height={45} />
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
